import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotice } from '../shared/NoticeProvider';
import type { SignalDetail } from '../../../types/signals-live';
import {
  deleteSignal,
  getOrganizationDisplaySettings,
  getSignalById,
  reclassifySignal,
  type OrganizationDisplaySettings,
} from '../../../utils/api';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import { useJunoContext } from '../../../contexts/JunoContext';

const DEFAULT_DISPLAY_SETTINGS: OrganizationDisplaySettings = {
  show_costs: true,
  show_langchain_details: true,
  root_cause_classification_enabled: true,
  root_cause_classification_changed_at: null,
};

export function useSignalsLiveDetailController(signalId: string | undefined) {
  const navigate = useNavigate();
  const { show: showNotice } = useNotice();
  const { updateViewContext } = useJunoContext();
  const { isConnected, subscribe, unsubscribe, addMessageListener } = useWebSocket();
  const [signal, setSignal] = useState<SignalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReclassifying, setIsReclassifying] = useState(false);
  const [displaySettings, setDisplaySettings] = useState<OrganizationDisplaySettings>(
    DEFAULT_DISPLAY_SETTINGS
  );

  const fetchSignalDetail = useCallback(async () => {
    if (!signalId) return;

    try {
      setLoading(true);
      setError(null);
      setSignal(await getSignalById(signalId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load signal details');
      console.error('Error fetching signal:', err);
    } finally {
      setLoading(false);
    }
  }, [signalId]);

  const fetchDisplaySettings = useCallback(async () => {
    try {
      setDisplaySettings(await getOrganizationDisplaySettings());
    } catch (err) {
      console.error('Error fetching display settings:', err);
      setDisplaySettings(DEFAULT_DISPLAY_SETTINGS);
    }
  }, []);

  useEffect(() => {
    const handleMessage = (data: any) => {
      if (data.type !== 'signal.updated' && data.type !== 'signal.classified') return;
      if (data.signal_id !== signalId) return;

      // Any terminal outcome clears the spinner, not just a successful
      // classification: a run that ends `failed` or `redaction_blocked` is just
      // as finished, and treating only `signal.classified` as terminal left the
      // reclassify button disabled and spinning until a refresh.
      const status = data.data?.processing_status;
      const isTerminal =
        data.type === 'signal.classified' ||
        status === 'failed' ||
        status === 'redaction_blocked';

      if (data.type === 'signal.classified') {
        setIsReclassifying(false);
        fetchSignalDetail();
      } else {
        setSignal((prev) => (prev ? { ...prev, ...data.data } : null));
        if (isTerminal) {
          setIsReclassifying(false);
        }
      }
    };

    return addMessageListener(handleMessage);
  }, [signalId, fetchSignalDetail, addMessageListener]);

  useEffect(() => {
    if (signalId) {
      fetchSignalDetail();
      fetchDisplaySettings();
      subscribe('signal', signalId);
    }

    return () => {
      if (signalId) {
        unsubscribe('signal', signalId);
      }
    };
  }, [signalId, subscribe, unsubscribe, fetchSignalDetail, fetchDisplaySettings]);

  useEffect(() => {
    if (signal && signalId) {
      updateViewContext('signal', {
        signalId,
        signalData: signal,
      });
    }

    return () => {
      updateViewContext(null, {});
    };
  }, [signal, signalId, updateViewContext]);

  const handleReclassify = useCallback(async () => {
    if (!signalId || isReclassifying) return;

    try {
      setIsReclassifying(true);
      const { status } = await reclassifySignal(signalId);
      if (status === 'already_classifying' || status === 'import_pending') {
        // The busy flag is otherwise cleared only by the realtime handler, on
        // `signal.classified` or a terminal state. A refusal publishes nothing,
        // so without this the spinner runs until the user navigates away.
        setIsReclassifying(false);
        showNotice({
          type: 'info',
          message:
            status === 'already_classifying'
              ? 'This signal is already being classified.'
              : 'This signal is waiting for its import to finish.',
        });
      }
    } catch (err) {
      console.error('Failed to reclassify signal:', err);
      setIsReclassifying(false);
      showNotice({ type: 'error', message: 'Failed to reclassify signal. Please try again.' });
    }
  }, [signalId, isReclassifying, showNotice]);

  const confirmDelete = useCallback(async () => {
    if (!signalId) return;

    try {
      setIsDeleting(true);
      await deleteSignal(signalId);
      navigate('/v4/records');
    } catch (err) {
      console.error('Error deleting signal:', err);
      showNotice({ type: 'error', message: 'Failed to delete signal. Please try again.' });
    } finally {
      setIsDeleting(false);
    }
  }, [signalId, navigate, showNotice]);

  return {
    signal,
    loading,
    error,
    isConnected,
    isDeleting,
    isReclassifying,
    displaySettings,
    confirmDelete,
    handleReclassify,
  };
}
